import { portalInvoiceRefetchInterval } from './portal-invoice-poll';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalGet, portalPost } from '@/lib/portal/customerClient';
import { usePortalAuthStore } from '@/stores/portalAuth.store';
import { ApiError } from '@/lib/api/errors';
import type {
  CreateStripeSessionResponse,
  PublicInvoicePaymentConfig,
  PublicLineItem,
} from '@/types/public';

export interface PortalInvoiceListItem {
  id: string;
  invoiceNumber: string;
  status: string;
  total: number;
  balanceDue: number;
  /** What is still owed once the customer's bank money still processing lands. */
  amountDue: number;
  /** Their bank money still processing on this invoice, 0 when none. */
  processingAmount: number;
  /** Date-only calendar values — render with a date-only formatter, not a tz
   * instant, or the day shifts for customers west of UTC. */
  issueDate: string | null;
  dueDate: string | null;
}

/**
 * A portal invoice line IS a public invoice line.
 *
 * `ContactPortalInvoicesService.detail` proves ownership and then spreads
 * `InvoicesService.findByIdPublic` — the very same projection the public share
 * link renders — so these rows come off `mapInvoiceLineItemsForPublic` and
 * nothing narrows them on the way. Declaring a shorter shape here did not make
 * the payload smaller; it only hid the fields the shared customer-document
 * helpers read, so the portal was the one invoice surface that could not apply
 * the visibility rule and printed a material nobody is being charged for.
 *
 * `Partial<>` because the portal only ever READS these rows, and a customer-safe
 * projection is entitled to omit a field it has nothing to say about.
 */
export type PortalInvoiceLineItem = Partial<PublicLineItem>;

/** The customer-safe invoice detail projection (a subset of the public shape). */
export interface PortalInvoiceDetail {
  id: string;
  invoiceNumber: string;
  status: string;
  lineItems: PortalInvoiceLineItem[];
  subtotal: number;
  taxTotal: number;
  discountTotal: number;
  membershipDiscountTotal?: number;
  tipAmount: number;
  creditsApplied: number;
  total: number;
  amountPaid: number;
  balanceDue: number;
  clearingBankTotal?: number | null;
  clearingBankCash?: number | null;
  provisionalClearingTotal?: number | null;
  clearingSince?: string | null;
  /** What can be paid online now; see `publicPayableAmount`. */
  payableBalance?: number | null;
  /** The business timezone, for the processing footnote's calendar day. */
  timezone?: string | null;
  /** Server card price difference figures (see `invoiceTotalPaid`). */
  depositApplied?: number;
  depositServiceFeeAmount?: number;
  paymentServiceFeeAmount?: number;
  date?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  /** Wisetack financing CTA when the invoice carries an offer (else null). */
  wisetack?: import('@/types/public').PublicWisetackCta | null;
  business?: {
    name?: string | null;
    logo?: string | null;
    address?: string | null;
    phone?: string | null;
    email?: string | null;
    website?: string | null;
    invoiceFooterText?: string | null;
    contractorLicense?: string | null;
    regulatoryNotice?: string | null;
  } | null;
  /** The linked job, when the invoice was raised on one — jobNumber + formatted
   * service address come from the shared public mapper; technicianName is added by
   * the portal service. Every field is optional / data-gated. */
  job?: {
    jobNumber?: string | null;
    jobType?: string | null;
    serviceAddress?: string | null;
    technicianName?: string | null;
  } | null;
  billToContact?: Record<string, unknown> | null;
  /** The invoice's contact — needed as the Payabli payor (customerNumber = id). */
  contact?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  /** Processor + (Payabli) embedded-component config, so the page renders the
   * right pay widget. Resolved from the session tenant, never a client value. */
  payment?: PublicInvoicePaymentConfig;
  /**
   * SERVER-derived: an inline Payabli ACH charge reached the provider and is
   * still clearing. The page must never offer a live pay CTA over money in
   * flight, and it can only know that from the server — an in-memory "submitted"
   * flag dies on reload, which is exactly how the portal used to re-offer the
   * full balance over an unsettled bank transfer.
   */
  paymentAttemptSettling?: boolean;
  /** The newest payment's status, so a settle that comes back FAILED surfaces. */
  latestPaymentStatus?: string;
}

/**
 * The server's card service-fee disclosure block, shared by the portal's
 * Stripe and Payabli pay surfaces. `feeCents` is the EXACT fee the charge path
 * will add (network cap + cost-of-acceptance cap already applied) — render it,
 * never a locally computed base x percent.
 */
export type PortalSurcharge = {
  enabled: boolean;
  surchargePercent: number;
  achCheckDiscountEnabled?: boolean;
  achCheckDiscountPercent?: number;
  discountCents?: number;
  feeCents: number | null;
};

/** The signed-in contact's invoices (newest first). */
export function usePortalInvoices() {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'invoices', token],
    queryFn: () => portalGet<PortalInvoiceListItem[]>('/api/portal/invoices'),
    enabled: Boolean(token),
    retry: false,
  });
}

/**
 * A 404/403 means this invoice will never load; anything else (a dropped
 * request, a 5xx, an offline blink) is transient. `retry: false` turns one
 * failed refetch into an errored query, so conflating the two ended the
 * settlement wait on a single blip.
 */
function isFatalPortalInvoiceError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 403);
}

/**
 * One invoice the contact owns; 404s (via ApiError) for anything else.
 *
 * Polls while a Payabli ACH charge is still clearing. The flag is SERVER-derived,
 * so a reload (or a second device) resumes the poll rather than dropping the
 * customer back onto a live pay button over money already in flight; the poll
 * stops on its own when the webhook settles the capture (balance drops / the
 * attempt goes FAILED).
 *
 * `awaitingStripeSettlement` covers the other case: a payer just back from
 * Stripe Checkout. Stripe redirects BEFORE its webhook lands, so a single
 * refetch on return always reads the still-unpaid invoice. See
 * `portalInvoiceRefetchInterval`.
 */
export function usePortalInvoice(
  id: string,
  awaitingStripeSettlement = false,
  settlementStartedAt: number | null = null,
) {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'invoice', id, token],
    queryFn: () => portalGet<PortalInvoiceDetail>(`/api/portal/invoices/${encodeURIComponent(id)}`),
    enabled: Boolean(token && id),
    retry: false,
    // The page renders its own load-failure card, so the client's error toast
    // only ever duplicated it — and while settling this polls every 1.5s
    // through transient failures, stacking a fresh toast on each dropped tick.
    meta: { suppressToast: true },
    // `elapsedMs` is read fresh on every tick, so the poll expires on its own
    // schedule rather than waiting for a render to notice.
    refetchInterval: (query) =>
      portalInvoiceRefetchInterval(
        query.state.data as PortalInvoiceDetail | undefined,
        awaitingStripeSettlement,
        isFatalPortalInvoiceError(query.state.error),
        settlementStartedAt === null ? 0 : Date.now() - settlementStartedAt,
      ),
    // `undefined` falls through to the client default, which is TanStack's
    // `true`. Passing `false` here silently opted EVERY normal invoice visit
    // out of focus refetching — a regression, since the query was previously
    // unset. While settling, `'always'` rather than `true`: the client sets
    // `staleTime: 60_000`, so a plain `true` would skip the refetch for a payer
    // who tabs back inside the first minute, which is most of them.
    refetchOnWindowFocus: awaitingStripeSettlement ? 'always' : undefined,
  });
}

export interface PortalPaymentEvent {
  id: string;
  kind: 'charge' | 'refund';
  /** Signed: positive for a charge, negative for a refund. */
  amount: number;
  status: string;
  paymentMethod: string;
  /** `Payment.flow`: `CHEQUE_SCAN` marks a scanned check stored as ACH. */
  flow?: string | null;
  /** Event instant (rendered in the tenant tz). */
  date: string;
  receipt: { id: string; number: string } | null;
}

export interface PortalPaymentHistory {
  timezone: string | null;
  payments: PortalPaymentEvent[];
}

/** The customer-visible payment history for one of the contact's invoices. */
export function usePortalInvoicePayments(id: string, enabled = true) {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'invoice', id, 'payments', token],
    queryFn: () =>
      portalGet<PortalPaymentHistory>(`/api/portal/invoices/${encodeURIComponent(id)}/payments`),
    enabled: Boolean(token && id) && enabled,
    retry: false,
  });
}

export interface PortalPayabliChargeResult {
  status: string;
  amount: number;
  surchargeAmount: number;
}

/**
 * Start a Stripe Checkout session for one of the contact's own invoices. Returns
 * the hosted-checkout `url` to redirect to; the amount and tenant are resolved
 * server-side (the body is empty). Money moves later on Stripe's webhook.
 */
export function usePortalCreateStripeSession(id: string) {
  return useMutation({
    // `method` is the rail the payer chose. 'card' additionally attests the
    // page rendered the card service-fee disclosure, which is what lets the
    // server put the fee on the session; omitting it keeps every accepted rail
    // and no fee.
    mutationFn: (body?: { method?: 'card' | 'ach' }) =>
      portalPost<CreateStripeSessionResponse>(
        `/api/portal/invoices/${encodeURIComponent(id)}/stripe-session`,
        body ?? {},
      ),
  });
}

/**
 * Charge one of the contact's own invoices via Payabli (embedded-component temp
 * token → immediate charge). The only input is the token; the amount is what
 * the invoice still owes beside any bank payment processing, resolved
 * server-side. On success, refetch the invoice + its
 * payment history so the page flips to the paid state.
 */
export function usePortalPayabliCharge(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { temporaryToken: string; surchargeDisclosed?: boolean }) =>
      portalPost<PortalPayabliChargeResult>(
        `/api/portal/invoices/${encodeURIComponent(id)}/payabli-charge`,
        input,
      ),
    onSuccess: () => {
      // Refetch the invoice + its payment history, and the list (its balance is
      // now stale). Prefix keys match the token-suffixed query keys.
      void queryClient.invalidateQueries({ queryKey: ['portal', 'invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'invoices'] });
    },
  });
}
